import React, { useEffect } from "react";
import { cn } from "@/lib/utils";
import { AlertCircle, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge"; 
import { Button, buttonVariants } from "@/components/ui/button"; 
import { Progress } from "@/components/ui/progress"; 
import { FeatureTooltip } from "@/components/FeatureTooltip";
import { useSubscription } from "@/hooks/use-subscription";
import { useAnalytics } from "@/hooks/use-analytics";

interface QueryLimitAlertProps {
  remainingQueries: number;
  hasReachedLimit: boolean;
  maxQueries: number;
}

export const QueryLimitAlert = ({ 
  remainingQueries, 
  hasReachedLimit, 
  maxQueries 
}: QueryLimitAlertProps) => {
  const { openPricingModal } = useSubscription(); 
  const analytics = useAnalytics();

  const queriesUsed = maxQueries !== Infinity ? maxQueries - remainingQueries : 0;
  const queryPercentage = maxQueries !== Infinity && maxQueries > 0 
    ? Math.min(100, Math.round((queriesUsed / maxQueries) * 100))
    : 0;

  const getProgressColor = () => {
    if (queryPercentage >= 100) return "bg-destructive"; 
    if (queryPercentage >= 80) return "bg-yellow-500"; 
    return "bg-blue-500"; 
  };
  
  useEffect(() => {
    if (maxQueries === Infinity) return; 

    if (hasReachedLimit) {
      analytics.trackEvent('query_limit_reached', {
        remaining: remainingQueries,
        max_queries: maxQueries
      });
    } else if (queryPercentage >= 80) { 
      analytics.trackEvent('query_limit_warning', {
        remaining: remainingQueries,
        max_queries: maxQueries,
        percentage: queryPercentage
      });
    }
  }, [hasReachedLimit, remainingQueries, queryPercentage, analytics, maxQueries]);

  if (maxQueries === Infinity) {
    return null;
  }

  return (
    <div className={cn(
      "flex flex-col sm:flex-row sm:items-center justify-between",
      "py-2 px-4 mt-4 mb-2 border rounded-lg",
      "bg-background/80 backdrop-blur-sm shadow-sm", 
      hasReachedLimit ? "border-destructive/50" : "border-border/50" 
    )}>
      <div className="flex-grow space-y-1 mb-2 sm:mb-0 sm:mr-4">
        <div className="flex justify-between items-center text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Daily AI Queries</span>
          <span>{queriesUsed} / {maxQueries} used</span>
        </div>
        <Progress 
          value={queryPercentage} 
          className={cn("h-2", getProgressColor())} 
        />
        {hasReachedLimit && (
          <div className="flex items-center text-xs text-destructive mt-1">
            <AlertCircle className="h-3.5 w-3.5 mr-1" />
            Daily limit reached.
          </div>
        )}
      </div>
      
      <Button 
        onClick={() => openPricingModal('query_limit_alert')}
        variant={hasReachedLimit ? "destructive" : "default"} 
        size="sm"
        className="text-xs whitespace-nowrap flex-shrink-0"
      >
        Upgrade to Premium
      </Button>
    </div>
  );
};